/* End-to-end encryption helpers using WebCrypto. Hybrid: RSA-OAEP-2048 + AES-GCM-256. */

const RSA_PARAMS = {
  name: "RSA-OAEP",
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: "SHA-256",
};
const RSA_USAGES_PRIV = ["decrypt"];
const RSA_USAGES_PUB = ["encrypt"];
const AES_PARAMS = { name: "AES-GCM", length: 256 };
const AES_USAGES = ["encrypt", "decrypt"];

function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64ToBuf(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes.buffer;
}

export async function generateKeypair() {
  const kp = await crypto.subtle.generateKey(RSA_PARAMS, true, ["encrypt", "decrypt"]);
  const publicJwk = await crypto.subtle.exportKey("jwk", kp.publicKey);
  const privateJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  return { publicJwk, privateJwk };
}

async function importPub(jwk) {
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    RSA_USAGES_PUB
  );
}

async function importPriv(jwk) {
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    RSA_USAGES_PRIV
  );
}

/**
 * Encrypt a plaintext message for multiple recipients (hybrid).
 *   recipientPubJwks: { [userId]: publicJwk }
 * Returns: { iv, ciphertext, aes_for: { [userId]: rsa_wrapped_aes_key } }
 */
export async function encryptForRecipients(plaintext, recipientPubJwks) {
  // 1. Generate AES key + IV
  const aesKey = await crypto.subtle.generateKey(AES_PARAMS, true, AES_USAGES);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // 2. AES-encrypt the plaintext
  const encoded = new TextEncoder().encode(plaintext);
  const ctBuf = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, encoded);

  // 3. Export raw AES key bytes
  const rawAes = await crypto.subtle.exportKey("raw", aesKey);

  // 4. RSA-wrap the raw AES key for each recipient's public key
  const aes_for = {};
  for (const [userId, pubJwk] of Object.entries(recipientPubJwks)) {
    if (!pubJwk) continue;
    const pub = await importPub(pubJwk);
    const wrapped = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, pub, rawAes);
    aes_for[userId] = bufToB64(wrapped);
  }

  return {
    iv: bufToB64(iv.buffer),
    ciphertext: bufToB64(ctBuf),
    aes_for,
  };
}

/**
 * Decrypt a message using your private key.
 *   wrappedAesB64: my rsa_wrapped_aes_key
 *   ivB64, ciphertextB64: from the message
 *   privateJwk: my stored private JWK
 */
export async function decryptForMe({ wrappedAesB64, ivB64, ciphertextB64, privateJwk }) {
  const priv = await importPriv(privateJwk);
  const rawAesBuf = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    priv,
    b64ToBuf(wrappedAesB64)
  );
  const aesKey = await crypto.subtle.importKey("raw", rawAesBuf, AES_PARAMS, false, AES_USAGES);
  const ptBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(b64ToBuf(ivB64)) },
    aesKey,
    b64ToBuf(ciphertextB64)
  );
  return new TextDecoder().decode(ptBuf);
}
